import React, {memo, Fragment, useState} from 'react';
import {Display, Tabs, Chart, Icon, Duration, DurationTagPicker, Flex, Card, Scrollable, Activities, HorizontalScroller} from 'components';
import {returnNull, get, modal, noop, useWindowSize, useNamedSelector} from 'helpers';
import {updateStatus} from 'store/activities';
import css from './timelog.module.scss';

export function Timelog() {
  return (
    <main className={css('bleed', 'timelog')}>
      <HorizontalScroller columns={Math.round(useWindowSize().width/400)} Render={Column} max={0} />
    </main>
  );
}

const Column = memo(({ position }) => {
  const { today } = useNamedSelector('calendar.days');
  const date = today.clone().add(position, 'day');
  const isEditable = useNamedSelector('relations.isEditable');
  const list = useNamedSelector('calendar.day', {date}).filter(isEditable);
  return (
    <div>
      <header className={css.header}>
        { /day|tomorrow/i.test(date.calendar()) ? date.calendar() : date.format('ddd, MMM D') }
      </header>
      <Scrollable className={['stretch-to-bottom', css.scrollable]}>
        { list.map(activity =>
          <Activities.Card
            ActionMenu={returnNull}
            Body={Body}
            context={{date}}
            Footer={false}
            id={activity.id}
            key={activity.id}
            onClick={noop}
            {...activity}
          />
        ) }
      </Scrollable>
    </div>
  );
});

function Body({ id, name, duration, repeatType, context: { date } }) {
  const timer = useNamedSelector('calendar.timer', { id, date });
  return (
    <Fragment>
      <Card.Body>
        <Card.Text>{ name }</Card.Text>
        <Display what={duration ? Card.Progress : Footer} now={Math.round(timer.getResult()/60)} max={duration}>
          <Flex className={['middle', 'justified', 'bigger']}>
            <DurationTagPicker
              value={Math.round(timer.getResult()/60)}
              setValue={value => updateStatus({ id, contextDate: date, result: value*60 })}
              placeholder="+ time"
            />
            { duration > 0 &&
              <Duration value={duration*60} hideSeconds />
            }
          </Flex>
        </Display>
      </Card.Body>
      { repeatType &&
        <Card.Toolbar>
          <Icon name="pencil" onClick={modal(Activities.Single.EditForm, {id, contextDate: date})} />
          <Icon name="chart" onClick={modal(TimelogChart, {id, name})} />
        </Card.Toolbar>
      }
    </Fragment>
  );
}

const Footer = ({children}) => (
  <div className="small-spacing small secondary">{children}</div>
);

function TimelogChart({ id, name, popup }) {
  const [ period, setPeriod ] = useState(7);
  const progress = useNamedSelector('calendar.index');
  const data = useNamedSelector('calendar.lastDays', { length: period }).map(moment => ({
    moment,
    value: get(progress, moment.unix() + '.' + id + '.result') || 0,
  }));
  return (
    <Fragment>
      <Flex className="justified">
        <div>
          <h1>{name}</h1>
          <small className="secondary">Timelog</small>
        </div>
        <Icon name="close" onClick={popup.close} />
      </Flex>
      <Tabs className="spacing">
        <Tabs.Tab pressed={period == 7} onClick={() => setPeriod(7) || popup.bounce()}>7 days</Tabs.Tab>
        <Tabs.Tab pressed={period == 30} onClick={() => setPeriod(30) || popup.bounce()}>30 days</Tabs.Tab>
        <Tabs.Tab pressed={period == 90} onClick={() => setPeriod(90) || popup.bounce()}>90 days</Tabs.Tab>
      </Tabs>
      <Chart
        className="spacing"
        data={data}
        detailed
        height={200}
        isTime
        type="bar"
        barPercentage={0.75}
      />
    </Fragment>
  );
}
